import { ErrorHandler, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject } from 'rxjs';
import * as padStart from 'lodash/padStart';

import { environment } from '../../../environments/environment';
import { IAuthResult } from '../models/auth-result.model';
import { IAuthEvent, AuthEventType } from '../models/auth-event.model';
import { ITicket } from '../models/ticket.model';


/**
 * @const
 * Cushion time (in seconds) for retrieving a new access token using the refresh token. A new access
 * token will be retrieved REFRESH_TOKEN_BUFFER seconds prior to the access token's expiration.
 */
const REFRESH_TOKEN_BUFFER = 30;


export interface IAuthService {
  /**
   * Event stream of authentication events (sign in, sign out, etc.)
   */
  readonly authStream: BehaviorSubject<IAuthEvent>;

  /**
   * Token stream
   */
  readonly tokenStream: BehaviorSubject<string>;

  /**
   * Returns true if the access token is still valid.
   */
  readonly authenticated: boolean;

  /**
   * Returns true if authenticated as an end user (as opposed to a client)
   */
  readonly endUser: boolean;

  /**
   * Returns the access token (if available).
   */
  readonly token: string;

  /**
   * Sends an authentication request to the authorization API to retrieve an access token. For anonymous access, this
   * logs in using client credentials; if the user is logged in to the AOPA.org website, this uses the resource owner
   * password credentials (ROPC) flow, using the AOPA ticket cookie as the password.
   */
  login(): Promise<IAuthResult>;

  /**
   * Method for redirecting user to the login page.
   */
  redirectToLogin(): void;

  /**
   * Logs off the current user by deleting the tokens from local storage
   */
  logout(): void;
}


/**
 * Authentication service
 */
@Injectable()
export class AuthService implements IAuthService {
  private _authStream: BehaviorSubject<IAuthEvent> = new BehaviorSubject<IAuthEvent>(null);
  private _tokenStream: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  private _refreshTimer;
  private _ticket: ITicket;
  private _scope: string;
  private _clientId: string;
  private _clientSecret: string;
  private _endUser: boolean;

  /**
   * Encoded access token sent with API requests.
   */
  private _accessToken: string|null = null;

  /**
   * Refresh token (used to get new access token when the old one is about to expire)
   */
  private _refreshToken: string|null = null;

  /**
   * Event stream of authentication events (sign in, sign out, etc.)
   */
  get authStream(): BehaviorSubject<IAuthEvent> {
    return this._authStream;
  }

  /**
   * Token stream
   */
  get tokenStream(): BehaviorSubject<string> {
    return this._tokenStream;
  }

  /**
   * Returns true if the access token is still valid.
   */
  get authenticated(): boolean {
    return !!this._accessToken;
  }

  /**
   * Returns true if authenticated as an end user (as opposed to a client)
   */
  get endUser(): boolean {
    return this._endUser;
  }

  /**
   * Returns the access token (if available).
   */
  get token(): string {
    return this._accessToken;
  }

  get recid(): string {
    return this._ticket.recid;
  }

  constructor(
    private http: HttpClient,
    private _cookies: CookieService,
    private _errorHandler: ErrorHandler
  ) { }

  /**
   * Sends an authentication request to the authorization API to retrieve an access token. For anonymous access, this
   * logs in using client credentials; if the user is logged in to the AOPA.org website, this uses the resource owner
   * password credentials (ROPC) flow, using the AOPA ticket cookie as the password.
   */
  async login(): Promise<IAuthResult> {
    const ticketCookie = this._cookies.get('Ticket');
    let request: any;
    if (ticketCookie) {
      this._ticket = this.parseTicket(ticketCookie);
      this._endUser = true;
      this._clientId = environment.clientIdUser;
      this._clientSecret = environment.clientSecretUser;
      this._scope = environment.authScopesUser;
      request = {
        grant_type: 'password',
        client_id: this._clientId,
        client_secret: this._clientSecret,
        username: this._ticket.username,
        password: ticketCookie,
        scope: this._scope
      };
    } else {
      this._endUser = false;
      this._clientId = environment.clientIdAnon;
      this._clientSecret = environment.clientSecretAnon;
      this._scope = environment.authScopesAnon;
      request = {
        grant_type: 'client_credentials',
        client_id: this._clientId,
        client_secret: this._clientSecret,
        scope: this._scope
      };
    }
    const result = await this._authenticate(request);
    if (result.success) {
      this._tokenStream.next(this._accessToken);
      this._authStream.next({
        type: AuthEventType.SignIn
      });
    } else {
      this._tokenStream.next(null);
      this._authStream.next({
        type: AuthEventType.InvalidSignIn
      });
    }
    return result;
  }

  /**
   * Method for redirecting user to the login page.
   */
  redirectToLogin() {
    window.location.href = environment.loginUrl;
  }

  /**
   * Logs off the current user by deleting the tokens
   */
  async logout() {
    this._accessToken = null;
    this._refreshToken = null;
    this._tokenStream.next(null);
    this._authStream.next({
      type: AuthEventType.SignOut
    });
  }

  /**
   * Parses the AOPA ticket cookie
   * @param {string} ticket Ticket cookie value as a string
   * @returns {ITicket} Ticket info
   */
  parseTicket(ticket: string): ITicket {
    const result = {} as ITicket;
    const decoded = decodeURIComponent(ticket);
    const parts = decoded.split('&');

    for (let i = 0; i < parts.length; i += 2) {
      const key = parts[i];
      const value = parts[i + 1];

      switch (key) {
        case 'user':
          result.username = value;
          break;
        case 'loop':
          const loop = parseInt(value, 10);
          if (loop && !isNaN(loop)) {
            const recid = (loop / 2).toString();
            result.recid = padStart(recid, 8, '0');
          }
          break;
      }
    }

    return result;
  }

  /**
   * Retrieves a new access token using the refresh token.
   */
  private async _refresh(): Promise<IAuthResult> {
    if (!this._refreshToken) {
      throw new Error('Missing refresh token');
    }
    return await this._authenticate({
      grant_type: 'refresh_token',
      refresh_token: this._refreshToken,
      client_id: this._clientId,
      client_secret: this._clientSecret,
      scope: this._scope
    });
  }

  /**
   * Sends an authentication request to the server
   * @param request Authentication request parameters
   * @param [refresh] True to schedule a refresh of the access token prior to its expiration
   */
  private async _authenticate(request, refresh = true): Promise<IAuthResult> {
    // Set up authentication request
    const url = `${environment.authUrl}/connect/token`;
    const options = { headers: new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded') };
    let params = new HttpParams();
    Object.keys(request).forEach(param => {
      const value = request[param];
      params = params.append(param, value);
    });

    // Send authentication request
    let result: IAuthResult;
    try {
      const data = await this.http.post<any>(url, params, options).toPromise();
      if (!data || !data.access_token) {
        throw new Error('Missing token from response');
      }
      result = {
        success: true,
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        expiresIn: data.expires_in
      } as IAuthResult;
    } catch (e) {
      if (e && e.json) {
        const r = e.json();
        return {
          success: false,
          error: r.error,
          errorDescription: r.error_description
        } as IAuthResult;
      }
      throw e;
    }

    // Store access token
    if (result.accessToken) {
      this._accessToken = result.accessToken;

      // Cancel any existing pending refresh of the access token
      this._cancelRefresh();
    } else {
      throw new Error('Missing token from response');
    }

    // Store refresh token
    if (result.refreshToken) {
      this._refreshToken = result.refreshToken;

      // Schedule a refresh of the access token prior to its expiration
      if (refresh) {
        this._scheduleRefresh(result.expiresIn);
      }
    }

    return result;
  }

  /**
   * Schedules a refresh of the access token prior to its expiration. If the user is not
   * authenticated, or the access token is already expired, the refresh will occur immediately.
   * @param expiresIn {Number} The lifetime in seconds of the access token.
   */
  private _scheduleRefresh(expiresIn) {
    this._cancelRefresh();
    let delay = 0;
    if (this.authenticated) {
      delay = Math.max(expiresIn - REFRESH_TOKEN_BUFFER, 0);
    }
    this._refreshTimer = setTimeout(() => this.login(), delay * 1000);
  }

  /**
   * Cancels any pending refresh of the access token.
   */
  private _cancelRefresh() {
    if (this._refreshTimer) {
      clearTimeout(this._refreshTimer);
    }
  }
}
